import { FC, Fragment, useState, useMemo } from 'react';
import { FileSearchOutlined } from '@ant-design/icons';
import {
	Button,
	Col,
	DatePicker,
	Divider,
	Form,
	Flex,
	Input,
	message,
	Row,
	Select,
	Table,
	TableProps,
	Typography,
	Tooltip,
} from 'antd';
import { useApp } from '@/App';
import { useModals } from '@/components/modals/ModalProvider';
import FleetTitle from '@/libs/antd/components/Title';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import {
	MonitoringSLASearchParam,
	useMonitoringSLASearch,
	useMonitoringSLASearchOptions,
} from '@/libs/fleet-api/services/monitoring';
import { useForm } from '@/libs/antd/form';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import rule from '@/libs/antd/rules';
import { formatCurrency } from '@/libs/antd/formater';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

dayjs.extend(utc);

const DurationTooltip: FC<{
	duration: string | null;
	startDate: string | null;
	endDate: string | null;
	startProcess: string | null;
	endProcess: string | null;
}> = ({ duration, startDate, endDate, startProcess, endProcess }) => {
	if (!duration) return <>-</>;

	const content = (
		<div>
			<div>
				start date:{' '}
				{startDate ? dayjs(startDate).format('DD/MM/YYYY - HH:mm:ss') : '-'}
			</div>
			<div>
				end date:{' '}
				{endDate ? dayjs(endDate).format('DD/MM/YYYY - HH:mm:ss') : '-'}
			</div>
			<div>start process: {startProcess ?? '-'}</div>
			<div>end process: {endProcess ?? '-'}</div>
		</div>
	);

	return (
		<Tooltip title={content}>
			<pre>{duration}</pre>
		</Tooltip>
	);
};

const DEFAULT_PARAM: MonitoringSLASearchParam = {
	pagination: {
		pageNumber: 1,
		pageSize: 100,
	},
	search: {
		by: '',
		value: '',
	},
	module: 'application',
	startDate: dayjs().subtract(2, 'month').utc(true).toISOString(),
	endDate: dayjs().utc(true).toISOString(),
};

const MonitoringSLAListPage: FC<MonitoringSLASearchParam> = (props) => {
	const { navigate } = useApp();
	const { openCustomerDetail, openCustomerGroupDetail } = useModals();

	const { form, fieldName } = useForm<MonitoringSLASearchParam>();

	const [param, setParam] = useState<MonitoringSLASearchParam>({
		...DEFAULT_PARAM,
		...props,
	});

	// Transform endDate for API calls (add 1 day)
	const apiParam = useMemo(
		() => ({
			...param,
			endDate:
				param.endDate &&
				dayjs(param.endDate).add(1, 'day').utc(true).toISOString(),
		}),
		[param],
	);

	const slaSearchOptionQuery = useMonitoringSLASearchOptions(param.module);
	const monitoringSLASearchQuery = useMonitoringSLASearch(apiParam);

	const { moduleOptions = [], searchByOptions = [] } =
		slaSearchOptionQuery.data?.data ?? {};

	const { applications = [], disbursements = [] } =
		monitoringSLASearchQuery.data?.data ?? {};

	const applicationTableProps: TableProps<(typeof applications)[number]> = {
		dataSource: applications?.map((record, key) => {
			return {
				...record,
				key,
			};
		}),
		pagination: {
			showSizeChanger: false,
			pageSize: 100,
		},
		columns: [
			{
				title: 'No',
				align: 'center',
				fixed: 'left',
				width: 1,
				render: (_, record) =>
					(param.pagination.pageNumber - 1) * param.pagination.pageSize +
					record.key +
					1,
			},
			{
				title: 'Customer ID',
				width: 120,
				render: (_, record) => record.customerId ?? '-',
			},
			{
				title: 'Nama Customer',
				fixed: 'left',
				width: 200,
				render: (_, record) => {
					return (
						<a
							href="#"
							onClick={(e) => {
								e.preventDefault();
								if (!record.customerId) return;
								openCustomerDetail(String(record.customerId));
							}}
						>
							{record.customerName ?? '-'}
						</a>
					);
				},
			},
			{
				title: 'Group BMPP',
				render: (_, record) => {
					return (
						<a
							href="#"
							onClick={(e) => {
								e.preventDefault();
								if (!record.bmppGroupId) return;
								openCustomerGroupDetail(record.bmppGroupId);
							}}
						>
							{record.bmppGroupName ?? '-'}
						</a>
					);
				},
			},
			{
				title: 'FRM',
				render: (_, record) => record.frmName,
			},
			{
				title: 'FBDH',
				render: (_, record) => record.fbdhName,
			},
			{
				title: 'Divisi',
				render: (_, record) => record.divisionName,
			},
			{
				title: 'Jenis Plafond',
				render: (_, record) => record.plafonType,
			},
			{
				title: 'Sumber Order',
				render: (_, record) => record.orderSource,
			},
			{
				title: 'Nilai Plafond',
				render: (_, record) => formatCurrency(record.plafonAmount ?? 0),
			},
			{
				title: 'Prospect ID',
				render: (_, record) => record.prospectId,
			},
			{
				title: 'Status Prospect',
				render: (_, record) => record.prospectStatus,
			},
			{
				title: 'No. NAP',
				render: (_, record) => record.napNo,
			},
			{
				title: 'No. SPF',
				render: (_, record) => record.spfNo,
			},
			{
				title: 'Data Customer Date',
				align: 'center',
				render: (_, record) =>
					record.customerCreationDate
						? dayjs(record.customerCreationDate).format('DD/MM/YYYY')
						: '-',
			},
			{
				title: 'Data Customer Duration',
				align: 'center',
				render: (_, record) => (
					<DurationTooltip
						duration={record.customerCreationDuration}
						startDate={record.customerCreationStartDateTime}
						endDate={record.customerCreationEndDateTime}
						startProcess={record.customerCreationStartProcess}
						endProcess={record.customerCreationEndProcess}
					/>
				),
			},
			{
				title: 'Prospecting Date',
				align: 'center',
				render: (_, record) =>
					record.prospectingDate
						? dayjs(record.prospectingDate).format('DD/MM/YYYY')
						: '-',
			},
			{
				title: 'Prospecting Duration',
				align: 'center',
				render: (_, record) => (
					<DurationTooltip
						duration={record.prospectingDuration}
						startDate={record.prospectingStartDateTime}
						endDate={record.prospectingEndDateTime}
						startProcess={record.prospectingStartProcess}
						endProcess={record.prospectingEndProcess}
					/>
				),
			},
			{
				title: 'Customer Checking Date',
				align: 'center',
				render: (_, record) =>
					record.customerCreationDate
						? dayjs(record.customerCreationDate).format('DD/MM/YYYY')
						: '-',
			},
			{
				title: 'Customer Checking Duration',
				align: 'center',
				render: (_, record) => (
					<DurationTooltip
						duration={record.customerCheckingDuration}
						startDate={record.customerCheckingStartDateTime}
						endDate={record.customerCheckingEndDateTime}
						startProcess={record.customerCheckingStartProcess}
						endProcess={record.customerCheckingEndProcess}
					/>
				),
			},
			{
				title: 'Pra-Survey Date',
				align: 'center',
				render: (_, record) =>
					record.praSurveyDate
						? dayjs(record.praSurveyDate).format('DD/MM/YYYY')
						: '-',
			},
			{
				title: 'Pra-Survey Duration',
				align: 'center',
				render: (_, record) => (
					<DurationTooltip
						duration={record.praSurveyDuration}
						startDate={record.praSurveyStartDateTime}
						endDate={record.praSurveyEndDateTime}
						startProcess={record.praSurveyStartProcess}
						endProcess={record.praSurveyEndProcess}
					/>
				),
			},
			{
				title: 'Survey Date',
				align: 'center',
				render: (_, record) =>
					record.surveyDate
						? dayjs(record.surveyDate).format('DD/MM/YYYY')
						: '-',
			},
			{
				title: 'Survey Duration',
				align: 'center',
				render: (_, record) => (
					<DurationTooltip
						duration={record.surveyDuration}
						startDate={record.surveyStartDateTime}
						endDate={record.surveyEndDateTime}
						startProcess={record.surveyStartProcess}
						endProcess={record.surveyEndProcess}
					/>
				),
			},
			{
				title: 'Laporan Kunjungan Date',
				align: 'center',
				render: (_, record) =>
					record.visitReportDate
						? dayjs(record.visitReportDate).format('DD/MM/YYYY')
						: '-',
			},
			{
				title: 'Laporan Kunjungan Duration',
				align: 'center',
				render: (_, record) => (
					<DurationTooltip
						duration={record.visitReportDuration}
						startDate={record.visitReportStartDateTime}
						endDate={record.visitReportEndDateTime}
						startProcess={record.visitReportStartProcess}
						endProcess={record.visitReportEndProcess}
					/>
				),
			},
			{
				title: 'Pipeline Hot Prospect Date',
				align: 'center',
				render: (_, record) =>
					record.pipelineHotProspectDate
						? dayjs(record.pipelineHotProspectDate).format('DD/MM/YYYY')
						: '-',
			},
			{
				title: 'Pipeline Hot Prospect Duration',
				align: 'center',
				render: (_, record) => (
					<DurationTooltip
						duration={record.pipelineHotProspectDuration}
						startDate={record.pipelineHotProspectStartDateTime}
						endDate={record.pipelineHotProspectEndDateTime}
						startProcess={record.pipelineHotProspectStartProcess}
						endProcess={record.pipelineHotProspectEndProcess}
					/>
				),
			},
			{
				title: 'NAP Date',
				align: 'center',
				render: (_, record) =>
					record.napDate ? dayjs(record.napDate).format('DD/MM/YYYY') : '-',
			},
			{
				title: 'NAP Duration',
				align: 'center',
				render: (_, record) => (
					<DurationTooltip
						duration={record.napDuration}
						startDate={record.napStartDateTime}
						endDate={record.napEndDateTime}
						startProcess={record.napStartProcess}
						endProcess={record.napEndProcess}
					/>
				),
			},
			{
				title: 'Risalah Date',
				align: 'center',
				render: (_, record) =>
					record.resolutionDate
						? dayjs(record.resolutionDate).format('DD/MM/YYYY')
						: '-',
			},
			{
				title: 'Risalah Duration',
				align: 'center',
				render: (_, record) => (
					<DurationTooltip
						duration={record.resolutionDuration}
						startDate={record.resolutionStartDateTime}
						endDate={record.resolutionEndDateTime}
						startProcess={record.resolutionStartProcess}
						endProcess={record.resolutionEndProcess}
					/>
				),
			},
			{
				title: 'SPF Date',
				align: 'center',
				render: (_, record) =>
					record.spfDate ? dayjs(record.spfDate).format('DD/MM/YYYY') : '-',
			},
			{
				title: 'SPF Duration',
				align: 'center',
				render: (_, record) => (
					<DurationTooltip
						duration={record.spfDuration}
						startDate={record.spfStartDateTime}
						endDate={record.spfEndDateTime}
						startProcess={record.spfStartProcess}
						endProcess={record.spfEndProcess}
					/>
				),
			},
			{
				title: 'Perjanjian Pembiayaan Date',
				align: 'center',
				render: (_, record) =>
					record.agreementDisbursementDate
						? dayjs(record.agreementDisbursementDate).format('DD/MM/YYYY')
						: '-',
			},
			{
				title: 'Perjanjian Pembiayaan Duration',
				align: 'center',
				render: (_, record) => (
					<DurationTooltip
						duration={record.agreementDisbursementDuration}
						startDate={record.agreementDisbursementStartDateTime}
						endDate={record.agreementDisbursementEndDateTime}
						startProcess={record.agreementDisbursementStartProcess}
						endProcess={record.agreementDisbursementEndProcess}
					/>
				),
			},
			{
				title: 'SLA Pengajuan',
				align: 'center',
				render: (_, record) => (
					<DurationTooltip
						duration={record.applicationSla}
						startDate={record.applicationSlaStartDateTime}
						endDate={record.applicationSlaEndDateTime}
						startProcess={record.applicationSlaStartProcess}
						endProcess={record.applicationSlaEndProcess}
					/>
				),
			},
			{
				title: 'View Detail',
				align: 'center',
				fixed: 'right',
				render: (_, record) => {
					return (
						<a
							href="#"
							onClick={(e) => {
								e.preventDefault();
								navigate('MonitoringSLAPage', {
									module: param.module,
									prosectId: record.prospectId,
									onBack: () => {
										navigate('MonitoringSLAListPage', param);
									},
								});
							}}
						>
							<FileSearchOutlined />
						</a>
					);
				},
			},
		],
	};

	const disbursementsTableProps: TableProps<(typeof disbursements)[number]> = {
		dataSource: disbursements?.map((record, key) => {
			return {
				...record,
				key,
			};
		}),
		pagination: {
			showSizeChanger: false,
			pageSize: 100,
		},
		columns: [
			{
				title: 'No',
				align: 'center',
				fixed: 'left',
				width: 1,
				render: (_, record) =>
					(param.pagination.pageNumber - 1) * param.pagination.pageSize +
					record.key +
					1,
			},
			{
				title: 'Customer ID',
				fixed: 'left',
				width: 120,
				render: (_, record) => record.customerId ?? '-',
			},
			{
				title: 'Nama Customer',
				fixed: 'left',
				width: 200,
				render: (_, record) => {
					return (
						<a
							href="#"
							onClick={(e) => {
								e.preventDefault();
								if (!record.customerId) return;
								openCustomerDetail(String(record.customerId));
							}}
						>
							{record.customerName ?? '-'}
						</a>
					);
				},
			},
			{
				title: 'Group BMPP',
				render: (_, record) => {
					return (
						<a
							href="#"
							onClick={(e) => {
								e.preventDefault();
								if (!record.bmppGroupId) return;
								openCustomerGroupDetail(record.bmppGroupId);
							}}
						>
							{record.bmppGroupName ?? '-'}
						</a>
					);
				},
			},
			{
				title: 'FRM',
				render: (_, record) => record.frmName,
			},
			{
				title: 'FBDH',
				render: (_, record) => record.fbdhName,
			},
			{
				title: 'Divisi',
				render: (_, record) => record.divisionName,
			},
			{
				title: 'Jenis Plafond',
				render: (_, record) => record.plafonType,
			},
			{
				title: 'Sumber Order',
				render: (_, record) => record.orderSource,
			},
			{
				title: 'Nilai Plafond',
				render: (_, record) => formatCurrency(record.plafonAmount ?? 0),
			},
			{
				title: 'No. MPP',
				render: (_, record) => record.mppNo ?? '-',
			},
			{
				title: 'NTF',
				render: (_, record) => formatCurrency(record.ntfAmount ?? 0),
			},
			{
				title: 'Status MPP',
				render: (_, record) => record.mppStatus ?? '-',
			},
			{
				title: 'No. Simulasi',
				render: (_, record) => record.simulationNo ?? '-',
			},
			{
				title: 'No. PO',
				render: (_, record) => record.purchaseOrderNo ?? '-',
			},
			{
				title: 'Simulasi Kredit Date',
				align: 'center',
				render: (_, record) =>
					record.creditSimulationDate
						? dayjs(record.creditSimulationDate).format('DD/MM/YYYY')
						: '-',
			},
			{
				title: 'Simulasi Kredit Duration',
				align: 'center',
				render: (_, record) => (
					<DurationTooltip
						duration={record.creditSimulationDuration}
						startDate={record.creditSimulationStartDateTime}
						endDate={record.creditSimulationEndDateTime}
						startProcess={record.creditSimulationStartProcess}
						endProcess={record.creditSimulationEndProcess}
					/>
				),
			},
			{
				title: 'MPP Date',
				align: 'center',
				render: (_, record) =>
					record.mppDate ? dayjs(record.mppDate).format('DD/MM/YYYY') : '-',
			},
			{
				title: 'MPP Duration',
				align: 'center',
				render: (_, record) => (
					<DurationTooltip
						duration={record.mppDuration}
						startDate={record.mppStartDateTime}
						endDate={record.mppEndDateTime}
						startProcess={record.mppStartProcess}
						endProcess={record.mppEndProcess}
					/>
				),
			},
			{
				title: 'PO Date',
				align: 'center',
				render: (_, record) =>
					record.purchaseOrderDate
						? dayjs(record.purchaseOrderDate).format('DD/MM/YYYY')
						: '-',
			},
			{
				title: 'PO Duration',
				align: 'center',
				render: (_, record) => (
					<DurationTooltip
						duration={record.purchaseOrderDuration}
						startDate={record.purchaseOrderStartDateTime}
						endDate={record.purchaseOrderEndDateTime}
						startProcess={record.purchaseOrderStartProcess}
						endProcess={record.purchaseOrderEndProcess}
					/>
				),
			},
			{
				title: 'Pencairan Pembiayaan Date',
				align: 'center',
				render: (_, record) =>
					record.disbursementDate
						? dayjs(record.disbursementDate).format('DD/MM/YYYY')
						: '-',
			},
			{
				title: 'Pencairan Pembiayaan Duration',
				align: 'center',
				render: (_, record) => (
					<DurationTooltip
						duration={record.disbursementDuration}
						startDate={record.disbursementStartDateTime}
						endDate={record.disbursementEndDateTime}
						startProcess={record.disbursementStartProcess}
						endProcess={record.disbursementEndProcess}
					/>
				),
			},
			{
				title: 'SLA Pencairan',
				align: 'center',
				render: (_, record) =>
					record.disbursementSla ? <pre>{record.disbursementSla}</pre> : '-',
			},
			{
				title: 'View Detail',
				align: 'center',
				fixed: 'right',
				render: (_, record) => {
					return (
						<a
							href="#"
							onClick={(e) => {
								e.preventDefault();
								navigate('MonitoringSLAPage', {
									module: param.module,
									prosectId: record.prospectId!,
									purchaseOrderId: record.purchaseOrderId ?? undefined,
									mppId: record.mppId,
									spfId: record.spfId,
									onBack: () => {
										navigate('MonitoringSLAListPage', param);
									},
								});
							}}
						>
							<FileSearchOutlined />
						</a>
					);
				},
			},
		],
	};

	const tableProps = {
		application: applicationTableProps,
		disbursement: disbursementsTableProps,
	}[param.module];

	const handleDownloadExcel = () => {
		try {
			const data =
				param.module === 'application' ? applications : disbursements;

			if (!data || data.length === 0) {
				message.warning('Tidak ada data untuk diunduh');
				return;
			}

			// Prepare data for Excel export based on module
			let excelData: Record<string, string | number>[] = [];
			let columnWidths: { wch: number }[] = [];
			let worksheetName: string;

			if (param.module === 'application') {
				const appData = data as NonNullable<typeof applications>;
				excelData = appData.map((record, index) => ({
					'No': index + 1,
					'Customer ID': record.customerId ?? '-',
					'Nama Customer': record.customerName ?? '-',
					'Group BMPP': record.bmppGroupName ?? '-',
					'FRM': record.frmName ?? '-',
					'FBDH': record.fbdhName ?? '-',
					'Divisi': record.divisionName ?? '-',
					'Jenis Plafond': record.plafonType ?? '-',
					'Sumber Order': record.orderSource ?? '-',
					'Nilai Plafond': record.plafonAmount
						? formatCurrency(record.plafonAmount)
						: '-',
					'Prospect ID': record.prospectId ?? '-',
					'Status Prospect': record.prospectStatus ?? '-',
					'No. NAP': record.napNo ?? '-',
					'No. SPF': record.spfNo ?? '-',
					'Data Customer Date': record.customerCreationDate
						? dayjs(record.customerCreationDate).format('DD/MM/YYYY')
						: '-',
					'Data Customer Duration': record.customerCreationDuration ?? '-',
					'Prospecting Date': record.prospectingDate
						? dayjs(record.prospectingDate).format('DD/MM/YYYY')
						: '-',
					'Prospecting Duration': record.prospectingDuration ?? '-',
					'Customer Checking Date': record.customerCreationDate
						? dayjs(record.customerCreationDate).format('DD/MM/YYYY')
						: '-',
					'Customer Checking Duration': record.customerCheckingDuration ?? '-',
					'Pra-Survey Date': record.praSurveyDate
						? dayjs(record.praSurveyDate).format('DD/MM/YYYY')
						: '-',
					'Pra-Survey Duration': record.praSurveyDuration ?? '-',
					'Survey Date': record.surveyDate
						? dayjs(record.surveyDate).format('DD/MM/YYYY')
						: '-',
					'Survey Duration': record.surveyDuration ?? '-',
					'Laporan Kunjungan Date': record.visitReportDate
						? dayjs(record.visitReportDate).format('DD/MM/YYYY')
						: '-',
					'Laporan Kunjungan Duration': record.visitReportDuration ?? '-',
					'Pipeline Hot Prospect Date': record.pipelineHotProspectDate
						? dayjs(record.pipelineHotProspectDate).format('DD/MM/YYYY')
						: '-',
					'Pipeline Hot Prospect Duration':
						record.pipelineHotProspectDuration ?? '-',
					'NAP Date': record.napDate
						? dayjs(record.napDate).format('DD/MM/YYYY')
						: '-',
					'NAP Duration': record.napDuration ?? '-',
					'SPF Date': record.spfDate
						? dayjs(record.spfDate).format('DD/MM/YYYY')
						: '-',
					'SPF Duration': record.spfDuration ?? '-',
					'Perjanjian Pembiayaan Date': record.agreementDisbursementDate
						? dayjs(record.agreementDisbursementDate).format('DD/MM/YYYY')
						: '-',
					'Perjanjian Pembiayaan Duration':
						record.agreementDisbursementDuration ?? '-',
					'SLA Pengajuan': record.applicationSla ?? '-',
				}));

				columnWidths = [
					{ wch: 5 }, // No
					{ wch: 15 }, // Customer ID
					{ wch: 20 }, // Nama Customer
					{ wch: 15 }, // Group BMPP
					{ wch: 15 }, // FRM
					{ wch: 15 }, // FBDH
					{ wch: 15 }, // Divisi
					{ wch: 15 }, // Jenis Plafond
					{ wch: 15 }, // Sumber Order
					{ wch: 15 }, // Nilai Plafond
					{ wch: 15 }, // Prospect ID
					{ wch: 15 }, // Status Prospect
					{ wch: 15 }, // No. NAP
					{ wch: 15 }, // No. SPF
					{ wch: 12 }, // Data Customer Date
					{ wch: 20 }, // Data Customer Duration
					{ wch: 12 }, // Prospecting Date
					{ wch: 20 }, // Prospecting Duration
					{ wch: 12 }, // Customer Checking Date
					{ wch: 20 }, // Customer Checking Duration
					{ wch: 12 }, // Pra-Survey Date
					{ wch: 20 }, // Pra-Survey Duration
					{ wch: 12 }, // Survey Date
					{ wch: 20 }, // Survey Duration
					{ wch: 12 }, // Laporan Kunjungan Date
					{ wch: 20 }, // Laporan Kunjungan Duration
					{ wch: 12 }, // Pipeline Hot Prospect Date
					{ wch: 20 }, // Pipeline Hot Prospect Duration
					{ wch: 12 }, // NAP Date
					{ wch: 20 }, // NAP Duration
					{ wch: 12 }, // SPF Date
					{ wch: 20 }, // SPF Duration
					{ wch: 12 }, // Perjanjian Pembiayaan Date
					{ wch: 20 }, // Perjanjian Pembiayaan Duration
					{ wch: 20 }, // SLA Pengajuan
				];

				worksheetName = 'SLA Application';
			} else {
				const disbData = data as NonNullable<typeof disbursements>;
				excelData = disbData.map((record, index) => ({
					'No': index + 1,
					'Customer ID': record.customerId ?? '-',
					'Nama Customer': record.customerName ?? '-',
					'Group BMPP': record.bmppGroupName ?? '-',
					'FRM': record.frmName ?? '-',
					'FBDH': record.fbdhName ?? '-',
					'Divisi': record.divisionName ?? '-',
					'Jenis Plafond': record.plafonType ?? '-',
					'Sumber Order': record.orderSource ?? '-',
					'Nilai Plafond': record.plafonAmount
						? formatCurrency(record.plafonAmount)
						: '-',
					'No. MPP': record.mppNo ?? '-',
					'NTF': record.ntfAmount ? formatCurrency(record.ntfAmount) : '-',
					'Status MPP': record.mppStatus ?? '-',
					'No. Simulasi': record.simulationNo ?? '-',
					'No. PO': record.purchaseOrderNo ?? '-',
					'Simulasi Kredit Date': record.creditSimulationDate
						? dayjs(record.creditSimulationDate).format('DD/MM/YYYY')
						: '-',
					'Simulasi Kredit Duration': record.creditSimulationDuration ?? '-',
					'MPP Date': record.mppDate
						? dayjs(record.mppDate).format('DD/MM/YYYY')
						: '-',
					'MPP Duration': record.mppDuration ?? '-',
					'PO Date': record.purchaseOrderDate
						? dayjs(record.purchaseOrderDate).format('DD/MM/YYYY')
						: '-',
					'PO Duration': record.purchaseOrderDuration ?? '-',
					'Pencairan Pembiayaan Date': record.disbursementDate
						? dayjs(record.disbursementDate).format('DD/MM/YYYY')
						: '-',
					'Pencairan Pembiayaan Duration': record.disbursementDuration ?? '-',
					'SLA Pencairan': record.disbursementSla ?? '-',
				}));

				columnWidths = [
					{ wch: 5 }, // No
					{ wch: 15 }, // Customer ID
					{ wch: 20 }, // Nama Customer
					{ wch: 15 }, // Group BMPP
					{ wch: 15 }, // FRM
					{ wch: 15 }, // FBDH
					{ wch: 15 }, // Divisi
					{ wch: 15 }, // Jenis Plafond
					{ wch: 15 }, // Sumber Order
					{ wch: 15 }, // Nilai Plafond
					{ wch: 15 }, // No. MPP
					{ wch: 15 }, // NTF
					{ wch: 15 }, // Status MPP
					{ wch: 15 }, // No. Simulasi
					{ wch: 15 }, // No. PO
					{ wch: 12 }, // Simulasi Kredit Date
					{ wch: 20 }, // Simulasi Kredit Duration
					{ wch: 12 }, // MPP Date
					{ wch: 20 }, // MPP Duration
					{ wch: 12 }, // PO Date
					{ wch: 20 }, // PO Duration
					{ wch: 12 }, // Pencairan Pembiayaan Date
					{ wch: 20 }, // Pencairan Pembiayaan Duration
					{ wch: 20 }, // SLA Pencairan
				];

				worksheetName = 'SLA Disbursement';
			}

			// Create worksheet
			const worksheet = XLSX.utils.json_to_sheet(excelData);
			worksheet['!cols'] = columnWidths;

			// Create workbook
			const workbook = XLSX.utils.book_new();
			XLSX.utils.book_append_sheet(workbook, worksheet, worksheetName);

			// Generate filename with current date and module
			const currentDate = dayjs().format('YYYYMMDD');
			const moduleName =
				param.module === 'application' ? 'Application' : 'Disbursement';
			const filename = `Monitoring_SLA_${moduleName}_${currentDate}.xlsx`;

			// Write file
			const excelBuffer = XLSX.write(workbook, {
				bookType: 'xlsx',
				type: 'array',
			});
			const dataBlob = new Blob([excelBuffer], {
				type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
			});

			saveAs(dataBlob, filename);
			message.success('File berhasil diunduh');
		} catch (error) {
			console.error('Error downloading Excel file:', error);
			message.error('Gagal mengunduh file');
		}
	};

	return (
		<Fragment>
			<FleetTitle style={{ marginBottom: 20 }}>Monitoring SLA</FleetTitle>

			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				initialValues={param}
				layout="horizontal"
				colon={false}
				labelAlign="left"
				labelCol={{ style: { width: 160 } }}
				style={{ marginBlock: 20 }}
				onFinish={(values) => {
					setParam({
						...values,
						pagination: {
							pageNumber: 1,
							pageSize: 10,
						},
					});
				}}
			>
				<Row
					gutter={40}
					align="bottom"
				>
					<Col flex="auto">
						<Form.Item
							label="Date Entry From"
							style={{ marginBottom: 20 }}
						>
							<Row
								wrap={false}
								align="middle"
								gutter={20}
							>
								<Col span={12}>
									<Form.Item
										noStyle
										name={fieldName('startDate')}
										getValueProps={(value) => ({
											value: value && dayjs(value),
										})}
										normalize={(value) =>
											value && dayjs(value).utc(true).toISOString()
										}
										rules={[rule.required()]}
										initialValue={param.startDate}
									>
										<DatePicker format="DD/MM/YYYY" />
									</Form.Item>
								</Col>
								<Col span={12}>
									<Form.Item
										noStyle
										name={fieldName('endDate')}
										getValueProps={(value) => ({
											value: value && dayjs(value),
										})}
										normalize={(value) =>
											value && dayjs(value).utc(true).toISOString()
										}
										rules={[rule.required()]}
										initialValue={param.endDate}
									>
										<DatePicker format="DD/MM/YYYY" />
									</Form.Item>
								</Col>
							</Row>
						</Form.Item>
						<Form.Item
							label="Modul"
							name={fieldName('module')}
							style={{ marginBottom: 20 }}
							initialValue={param.module}
						>
							<Select
								allowClear
								disabled={slaSearchOptionQuery.isLoading}
								loading={slaSearchOptionQuery.isFetching}
								options={moduleOptions.map((option) => {
									return {
										label: option.value,
										value: option.id,
									};
								})}
							/>
						</Form.Item>
						<Form.Item label="Kata Kunci">
							<Row
								gutter={20}
								wrap={false}
							>
								<Col span={12}>
									<Form.Item
										name={fieldName(['search', 'by'])}
										noStyle
									>
										<Select
											allowClear
											options={searchByOptions.map((option) => {
												return {
													label: option.value,
													value: option.id,
												};
											})}
											onSelect={() => {
												form.resetFields([fieldName(['search', 'value'])]);
											}}
											onClear={() => {
												form.resetFields([fieldName(['search'])]);
											}}
										/>
									</Form.Item>
								</Col>
								<Col span={12}>
									<Form.Item
										name={fieldName(['search', 'value'])}
										noStyle
									>
										<Input />
									</Form.Item>
								</Col>
							</Row>
						</Form.Item>
					</Col>
					<Col flex="none">
						<Form.Item>
							<Button
								block
								style={{ width: 200 }}
								type="primary"
								htmlType="submit"
								disabled={monitoringSLASearchQuery.isFetching}
								loading={monitoringSLASearchQuery.isFetching}
							>
								SEARCH
							</Button>
						</Form.Item>
					</Col>
				</Row>
			</Form>

			<Divider />
			<Typography.Title level={3}>List Monitoring SLA</Typography.Title>

			<Table
				{...(tableProps as TableProps<unknown>)}
				loading={monitoringSLASearchQuery.isFetching}
				bordered
				scroll={{ x: 'max-content' }}
			/>

			<Flex
				justify="end"
				style={{ marginTop: 20 }}
			>
				<Button
					type="primary"
					onClick={handleDownloadExcel}
				>
					Download
				</Button>
			</Flex>
		</Fragment>
	);
};

export default MonitoringSLAListPage;
